import {
  UMButton, UMFormStyles, UMR, UMSidePanel, UMStack, UMToolbarIconButton,
} from '../../../urbanmap/_components';
import type { ConsumptionType, SelectionInfo, SelectionTool } from './types';
import { ProdControlStyles } from './ProdControls';
import { NetworkControls } from './NetworkControls';
import type { useNetwork } from './useNetwork';
import { BuildingUpload, useBuildingUpload } from './BuildingUpload';
import { NetworkLegend } from './NetworkLegend';
import { PlantControls } from './PlantControls';
import { DensityControls } from './DensityControls';
import { SelectionSection } from './SelectionSection';
import { SelectionActions } from './SelectionActions';

// Variante locale de UMSelectionPanel : même géométrie, footer et verrouillage
// confirmés par HomeSidebarSelection*.vue. Aucun changement de _components.

function SummaryRow({ label, value, unit }: { label: string; value: number | null; unit?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
      <div style={{ fontSize: 14, lineHeight: '20px', color: UMR.textStrong }}>{label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.25 }}>
        <div style={{ fontSize: 16, lineHeight: '24px', fontWeight: 700, color: UMR.textStrong }}>
          {value === null ? 'Non disponible' : value.toLocaleString('fr-FR')}
        </div>
        {unit && <div style={{ fontSize: 12, lineHeight: UMR.lhXs, color: UMR.textMuted }}>{unit}</div>}
      </div>
    </div>
  );
}

export function SelectionPanel({ upload, network, onClose, summary, activeTool, selections, selectionId, consumption, onSelectionChange, onSelectionActions, onQuit, onCreate, onEditLegend }: {
  upload: ReturnType<typeof useBuildingUpload>;
  network: ReturnType<typeof useNetwork>;
  onSelectionActions: (message: string) => void;
  onQuit: () => void;
  onCreate: () => void;
  onEditLegend: () => void;
  selections: SelectionInfo[];
  /** null = aucune sélection courante (après « Quitter la sélection »). */
  selectionId: string | null;
  consumption: ConsumptionType;
  onSelectionChange: (id: string) => void;
  onClose: () => void;
  summary: { buildings: number; heatTotal: number | null; pipes: number };
  activeTool: SelectionTool | null;
}) {
  return (
    <UMSidePanel
      title="Sélection de batiments"
      onClose={onClose}
      footer={network.activeTool ? <>
        <UMButton variant="secondary" onClick={network.cancel}>Annuler</UMButton>
        <UMButton disabled={!network.canValidate} onClick={network.validate}>
          {network.activeTool === 'draw-pipe' ? 'Terminer et valider' : 'Valider'}
        </UMButton>
      </> : undefined}
    >
      <UMFormStyles />
      <ProdControlStyles />
      <UMStack gap={16}>
        <div>
          <label htmlFor="current-selection" style={{ display: 'block', marginBottom: 8, color: UMR.textLabel, fontSize: 12 }}>
            Sélection en cours
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <select id="current-selection" className="prod-control" value={selectionId ?? ''}
            onChange={(event) => onSelectionChange(event.target.value)}
            disabled={!!activeTool || !!network.activeTool || network.loading}
            style={{
              flex: 1, minWidth: 0, height: 32, padding: '0 16px',
              border: `1px solid ${UMR.border}`, borderRadius: 0,
              background: UMR.white, color: selectionId ? UMR.textStrong : UMR.textPlaceholder, fontFamily: UMR.font, fontSize: 14,
            }}
          >
            {/* Placeholder de production (HomeSearchBar.vue) quand aucune sélection n'est ouverte. */}
            {!selectionId && <option value="" disabled hidden>Aucune sélection</option>}
            {selections.map((s) => <option key={s.id} value={s.id} style={{ color: UMR.textStrong }}>{s.name}</option>)}
          </select>
          {selectionId
            ? <SelectionActions key={selectionId} onMessage={onSelectionActions} onQuit={onQuit} />
            : <UMToolbarIconButton icon="create_new_folder" label="Créer une sélection" onClick={onCreate} />}
          </div>
        </div>
        {!selectionId && <div style={{ fontSize: 14, lineHeight: '21px', color: UMR.textSecondary }}>
          Choisissez une sélection dans la liste ou créez-en une nouvelle.
        </div>}
        {selectionId && <>
        <div style={{
          display: 'flex', flexDirection: 'column', gap: 12, padding: '8px 16px',
          background: UMR.grey100, borderRadius: 8, border: `1px solid ${UMR.border}`,
        }}>
          <div style={{ fontSize: 12, lineHeight: UMR.lhXs, fontWeight: 500, color: UMR.textLabel }}>Résumé</div>
          <UMStack gap={16}>
            <SummaryRow label="Nombre de bâtiments" value={summary.buildings} />
            <SummaryRow label={consumption === 'Froid' ? 'Total froid simulé' : 'Total chaud simulé'} value={summary.heatTotal} unit="MWhu" />
            <SummaryRow label="Canalisations" value={summary.pipes} unit="ml" />
          </UMStack>
        </div>
        <div>
          <SelectionSection title="Bâtiments" defaultOpen>
            <BuildingUpload state={upload.files[selectionId]} disabled={!!activeTool || !!network.activeTool || network.loading}
              onChoose={(name) => upload.choose(selectionId, name)} />
          </SelectionSection>
          <SelectionSection title="Centrales de production">
            <PlantControls network={network} blocked={!!activeTool} />
          </SelectionSection>
          <SelectionSection title="Tracé de réseau">
            <UMStack gap={12}>
              <NetworkLegend density={network.density} visible={network.pipesVisible} onVisibilityChange={network.setPipesVisible}
                onEditLegend={onEditLegend} />
              <NetworkControls network={network} blocked={!!activeTool} />
            </UMStack>
          </SelectionSection>
          <SelectionSection title="Calcul de la densité linéaire">
            <DensityControls density={network.density} blocked={!!activeTool || !!network.activeTool || network.loading} />
          </SelectionSection>
        </div>
        </>}
      </UMStack>
    </UMSidePanel>
  );
}
